import { Segment, toDateTimeStr } from '@mi-front/core';

/**
 * Checks if a waveform data array contains enough silence (500 consecutive zeros)
 * to be considered a silent segment. Returns the first index where 500 consecutive zeros start,
 * or -1 if no such sequence is found.
 */
export function silenceThreshold(arr: number[]): number {
  let zeroCount = 0;

  for (let i = 0; i < arr.length; i++) {
    const value = arr[i];
    if (value === 0) {
      zeroCount++;
      if (zeroCount === 500) {
        return i - 499; // Return the index where the sequence started
      }
    } else {
      zeroCount = 0; // Reset counter when non-zero is found
    }
  }

  return -1; // No sequence of 500 consecutive zeros found
}

/**
 * Splits a segment into smaller chunks when it contains silence
 */
export function splitTimestamps(segment: Segment): Segment[] {
  const CHUNK_SIZE = 8;
  const interval = (segment.endTs - segment.startTs) / CHUNK_SIZE;
  const chunkSize = Math.ceil(segment.waveform.data.length / CHUNK_SIZE);
  const result: Segment[] = [];
  let chunkStart = 0;
  let chunkEnd = chunkSize;

  for (let i = 0; i < CHUNK_SIZE; i++) {
    const newChunck = segment.waveform.data.slice(chunkStart, chunkEnd);
    const chunkStartTs = segment.startTs + i * interval;
    const chunkEndTs = segment.startTs + (i + 1) * interval;

    result.push({
      ...segment,
      id: chunkStartTs,
      startTs: chunkStartTs,
      endTs: chunkEndTs,
      duration: segment.duration / CHUNK_SIZE,
      waveform: {
        ...segment.waveform,
        length: newChunck.length / 3,
        data: newChunck,
      },
      dateStr: segment.dateStr ?? toDateTimeStr(chunkStartTs),
    });

    chunkStart = chunkEnd;
    chunkEnd += chunkSize;
  }

  return result;
}

/**
 * Processes segments to split silent ones into smaller chunks
 */
export function processSilentSegments(segments: readonly Segment[]): Segment[] {
  return segments
    .map((segment) => {
      if (!segment.waveform) {
        return segment;
      }

      const silenceThresholdIndex = silenceThreshold(segment.waveform.data);
      if (silenceThresholdIndex !== -1) {
        return splitTimestamps(segment);
      }

      return segment;
    })
    .flat();
}
